<template>
  <div class="space-y-3">
    <!-- Create Card -->
    <div class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <div class="bg-gray-50 border-b border-gray-300 px-4 py-2.5 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700">Add Departments</h3>
        <span class="text-xs text-gray-400">Group departments by faculty before submitting the full batch.</span>
      </div>

      <div class="p-3 space-y-3">
        <!-- Faculty Selector -->
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-600">Faculty *</label>
          <Select
            v-model="currentFacultyId"
            :options="allFaculties"
            optionLabel="faculty_name"
            optionValue="id"
            placeholder="Select a faculty"
            class="w-full"
            filter
            data-testid="department-faculty-select"
            @show="refreshFaculties"
          />
        </div>

        <!-- Type Selector -->
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-600">Type *</label>
          <Select
            v-model="currentType"
            :options="typeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            data-testid="department-type-select"
          />
        </div>

        <!-- Department Input -->
        <form class="flex gap-2 items-end" @submit.prevent="addDepartment">
          <div class="flex-1 flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Department Name *</label>
            <InputText
              v-model="departmentName"
              class="w-full"
              placeholder="e.g. Computer Science"
              :disabled="!currentFacultyId"
              :class="{ 'p-invalid': getErrorMessage('department_name') }"
              data-testid="department-name-input"
            />
            <small v-if="getErrorMessage('department_name')" class="text-red-500">
              {{ getErrorMessage('department_name') }}
            </small>
          </div>
          <Button
            type="submit"
            label="Add"
            icon="pi pi-plus"
            size="small"
            :disabled="!departmentName.trim() || !currentFacultyId"
            data-testid="department-add-btn"
          />
        </form>

        <!-- Current departments for this faculty -->
        <div v-if="currentDepartments.length">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-xs font-medium text-gray-700">
              Departments for this faculty
              <span class="inline-flex items-center justify-center bg-primary text-white text-[10px] font-bold rounded-full w-4 h-4 ml-1">
                {{ currentDepartments.length }}
              </span>
            </span>
          </div>

          <DataTable :value="currentDepartments" size="small" stripedRows>
            <Column header="#">
              <template #body="{ index }">
                <span class="text-gray-400 font-mono text-xs">{{ index + 1 }}</span>
              </template>
            </Column>
            <Column header="Department Name">
              <template #body="{ data }">
                <span class="text-sm font-medium">{{ data }}</span>
              </template>
            </Column>
            <Column>
              <template #body="{ index }">
                <Button
                  icon="pi pi-trash"
                  class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Remove'"
                  :data-testid="`department-remove-current-${index}`"
                  @click="removeCurrentDepartment(index)"
                />
              </template>
            </Column>
          </DataTable>
        </div>

        <Button
          label="Save to Batch"
          icon="pi pi-save"
          size="small"
          severity="secondary"
          outlined
          :disabled="!currentFacultyId || currentDepartments.length === 0"
          data-testid="department-save-batch-btn"
          @click="saveFacultyDepartments"
        />

        <!-- Batch Summary -->
        <div v-if="facultyDepartments.length" class="border border-gray-300 rounded-lg overflow-hidden">
          <div class="bg-gray-50 border-b border-gray-300 px-3 py-1.5">
            <span class="text-xs font-semibold text-gray-700">Batch Summary</span>
          </div>
          <DataTable :value="facultyDepartments" size="small">
            <Column field="facultyName" header="Faculty" />
            <Column header="Type">
              <template #body="{ data }">
                <span class="text-xs px-2 py-0.5 rounded-full" :class="data.type === 'academic' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'">
                  {{ data.type === 'academic' ? 'Academic' : 'Non-Teaching' }}
                </span>
              </template>
            </Column>
            <Column header="Departments">
              <template #body="{ data }">
                <div class="flex flex-wrap gap-1">
                  <span v-for="dept in data.departments" :key="dept"
                    class="inline-flex items-center text-xs px-2 py-0.5 rounded-full border"
                    :class="data.errors?.[dept] ? 'bg-red-50 text-red-700 border-red-200' : 'bg-primary/10 text-primary border-primary/20'">
                    {{ dept }}
                  </span>
                </div>
                <template v-for="dept in data.departments" :key="`${dept}-error`">
                  <small v-if="data.errors?.[dept]" class="block text-red-500 mt-0.5" :data-testid="`department-pending-error-${dept}`">
                    {{ dept }}: {{ data.errors[dept] }}
                  </small>
                </template>
              </template>
            </Column>
            <Column>
              <template #body="{ index }">
                <Button
                  icon="pi pi-trash"
                  class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Remove group'"
                  :data-testid="`department-remove-group-${index}`"
                  @click="facultyDepartments.splice(index, 1)"
                />
              </template>
            </Column>
          </DataTable>
        </div>

        <div class="flex justify-end pt-1.5 border-t border-gray-300">
          <Button
            label="Submit All Departments"
            icon="pi pi-check"
            size="small"
            severity="success"
            :loading="isSubmitting"
            :disabled="facultyDepartments.length === 0"
            data-testid="department-submit-all-btn"
            @click="submitAll"
          />
        </div>
      </div>
    </div>

    <!-- Existing Departments Card -->
    <div class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <div class="px-4 py-2.5 border-b border-gray-300 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700">
          Existing Departments
          <span v-if="paginatorInfo?.total" class="text-xs font-normal text-gray-400 ml-1">
            ({{ paginatorInfo.total }})
          </span>
        </h3>
      </div>

      <div class="p-0">
        <div v-if="loading" class="flex items-center justify-center py-8">
          <span class="pi pi-spin pi-spinner text-2xl text-primary"></span>
        </div>

        <DataTable
          v-else-if="departments.length"
          :value="departments"
          size="small"
          stripedRows
          :paginator="true"
          :rows="rowsPerPage"
          :totalRecords="paginatorInfo?.total ?? 0"
          :first="firstRecord"
          :lazy="true"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rowsPerPageOptions="[10, 25, 50]"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          @page="onPageChange"
        >
          <Column field="department_name" header="Department" />
          <Column header="Type">
            <template #body="{ data }">
              <span class="text-xs px-2 py-0.5 rounded-full" :class="data.type === 'non_teaching' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'">
                {{ data.type === 'non_teaching' ? 'Non-Teaching' : 'Academic' }}
              </span>
            </template>
          </Column>
          <Column header="Faculty">
            <template #body="{ data }">{{ facultyNameFor(data.faculty_id) }}</template>
          </Column>
          <Column header="Actions">
            <template #body="{ data }">
              <div class="flex gap-1">
                <Button
                  icon="pi pi-pencil"
                  class="p-button-secondary p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Edit'"
                  :data-testid="`department-edit-${data.id}`"
                  @click="openEdit(data)"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Delete'"
                  :loading="deletingId === data.id"
                  :data-testid="`department-delete-${data.id}`"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>

        <div v-else class="py-6 text-center text-gray-400 text-xs">No departments created yet</div>
      </div>
    </div>

    <!-- Edit Dialog -->
    <Dialog v-model:visible="showEdit" modal header="Edit Department" :style="{ width: '28rem' }">
      <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Department Name</label>
          <InputText
            v-model="editForm.department_name"
            placeholder="Department name"
            :class="{ 'p-invalid': getErrorMessage('department_name') }"
            data-testid="department-edit-name-input"
          />
          <small v-if="getErrorMessage('department_name')" class="text-red-500">
            {{ getErrorMessage('department_name') }}
          </small>
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Faculty</label>
          <Select
            v-model="editForm.faculty_id"
            :options="allFaculties"
            optionLabel="faculty_name"
            optionValue="id"
            placeholder="Select faculty"
            class="w-full"
            filter
            data-testid="department-edit-faculty-select"
          />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Type</label>
          <Select
            v-model="editForm.type"
            :options="typeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            data-testid="department-edit-type-select"
          />
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" text size="small" data-testid="department-edit-cancel-btn" @click="showEdit = false" />
        <Button
          label="Save"
          icon="pi pi-check"
          size="small"
          :loading="updatingDepartment"
          :disabled="!editForm.department_name.trim()"
          data-testid="department-edit-save-btn"
          @click="submitEdit"
        />
      </template>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useFaculties } from '@/composables/queries/useFaculties';
import { useDepartments } from '@/composables/queries/useDepartments';
import { useFormErrors } from '@/composables/useFormErrors';
import { submissionToast, useTertiaryEntityWorkflow } from '@/composables/useTertiaryEntityWorkflow';
import { type DepartmentBatchGroup, flattenDepartmentBatch, keepFailedDepartments } from '@/utils/departmentBatch';
import type { DepartmentType } from '@/types/composition-functions';

const toast = useToast();
const confirm = useConfirm();
const {
  createFacultyDepartment,
  updateTertiaryDepartment,
  deleteTertiaryDepartment,
  updatingDepartment,
} = useTertiaryMutations();
const { faculties: allFaculties, refreshFaculties } = useFaculties();
const {
  departments,
  paginatorInfo,
  loading,
  setPage,
  setItemsPerPage,
  reload,
} = useDepartments({ autoFetch: true, first: 10, page: 1 });
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

const typeOptions = [
  { label: 'Academic', value: 'academic' },
  { label: 'Non-Teaching', value: 'non_teaching' },
];

// ─── Create batch ────────────────────────────────────────────────
const facultyDepartments = ref<DepartmentBatchGroup[]>([]);
const currentFacultyId = ref('');
const currentType = ref('academic');
const departmentName = ref('');
const currentDepartments = ref<string[]>([]);
const { isSubmitting, submitAll: submitQueued } =  useTertiaryEntityWorkflow();
function addDepartment() {
  if (departmentName.value.trim()) {
    currentDepartments.value.push(departmentName.value.trim());
    departmentName.value = '';
  }
}

function removeCurrentDepartment(idx: number) {
  currentDepartments.value.splice(idx, 1);
}

function saveFacultyDepartments() {
  if (!currentFacultyId.value || currentDepartments.value.length === 0) return;
  const facultyName = allFaculties.value.find(f => f.id === currentFacultyId.value)?.faculty_name || '';
  facultyDepartments.value.push({
    facultyId: currentFacultyId.value,
    facultyName,
    type: currentType.value,
    departments: [...currentDepartments.value],
  });
  currentFacultyId.value = '';
  currentType.value = 'academic';
  currentDepartments.value = [];
  toast.add({ severity: 'info', summary: 'Saved', detail: `${facultyName} departments added to batch`, life: 2000 });
}

async function submitAll() {
  clearErrors();

  const result = await submitQueued(flattenDepartmentBatch(facultyDepartments.value), async(item) => {
    await createFacultyDepartment({
      department_name: item.dept,
      faculty_id: item.facultyId,
      type: item.type,
    });
  });

  facultyDepartments.value = keepFailedDepartments(facultyDepartments.value, result.failures);

  const summary = submissionToast(result, 'department(s)');
  if (summary) toast.add(summary);

  await reload();
}

// ─── Pagination ──────────────────────────────────────────────────
const rowsPerPage = computed(() => Number(paginatorInfo.value?.perPage) || 10);
const firstRecord = computed(
  () => (((Number(paginatorInfo.value?.currentPage) || 1) - 1) * rowsPerPage.value) || 0,
);

async function onPageChange(event: { first: number; rows: number; page?: number }) {
  if (event.rows !== rowsPerPage.value) {
    await setItemsPerPage(event.rows);
  } else if (typeof event.page === 'number') {
    await setPage(event.page + 1);
  } else {
    await setPage(Math.floor(event.first / event.rows) + 1);
  }
}

// ─── Lookup faculty name ─────────────────────────────────────────
function facultyNameFor(id: string | number): string {
  return allFaculties.value.find(f => String(f.id) === String(id))?.faculty_name || '—';
}

// ─── Edit ────────────────────────────────────────────────────────
const showEdit = ref(false);
const editingId = ref<string | null>(null);
const editForm = reactive<{ department_name: string; faculty_id: string; type: string }>({
  department_name: '',
  faculty_id: '',
  type: 'academic',
});

function openEdit(item: DepartmentType) {
  clearErrors();
  editingId.value = item.id;
  editForm.department_name = item.department_name;
  editForm.faculty_id = String(item.faculty_id);
  editForm.type = item.type || 'academic';
  showEdit.value = true;
}

async function submitEdit() {
  if (!editingId.value) return;
  clearErrors();
  try {
    const result = await updateTertiaryDepartment(editingId.value, {
      department_name: editForm.department_name.trim(),
      faculty_id: editForm.faculty_id,
      type: editForm.type,
    });
    if (result) {
      showEdit.value = false;
      editingId.value = null;
      reload();
    }
  } catch (err) {
    handleServerErrors(err);
  }
}

// ─── Delete ──────────────────────────────────────────────────────
const deletingId = ref<string | null>(null);

function confirmDelete(item: DepartmentType) {
  confirm.require({
    message: `Delete "${item.department_name}"? Programmes under it may be affected.`,
    header: 'Delete Department',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: async() => {
      deletingId.value = item.id;
      try {
        const ok = await deleteTertiaryDepartment(item.id);
        if (ok) reload();
      } finally {
        deletingId.value = null;
      }
    },
  });
}
</script>
